import * as React from "react";
import parse from "html-react-parser";

import { withJsonFormsControlProps } from "@jsonforms/react";
import { ControlElement, ControlProps } from "@jsonforms/core";

import Checkbox from "@ingka/checkbox";
import FormField from "@ingka/form-field";

import { CheckboxControlWrapper } from "../../styles";
import { findAndTransformHTMLString } from "../utils";
import { useIsUserCanOnlyEnableProperties } from "../../../hooks/useIsUserCanOnlyEnableProperties";

interface CheckboxProps extends ControlProps {
	uischema: {
		subText?: string;
	} & ControlElement;
}

const CheckboxControl: React.FC<CheckboxProps> = ({
	data,
	handleChange,
	path,
	label,
	enabled,
	schema,
	uischema,
	description,
}) => {
	const [canEnableOnly, setCanEnableOnly] = React.useState(false);

	const userCanOnlyEnableProperties = useIsUserCanOnlyEnableProperties();

	const onChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			if (userCanOnlyEnableProperties && !e.target.checked && data) {
				setCanEnableOnly(true);
				return;
			}
			handleChange(path, e.target.checked);
		},
		[userCanOnlyEnableProperties, data, handleChange, path]
	);

	const checkbox = React.useMemo(() => {
		const checkBoxlabel = label === "Multi" ? "Text area" : label;
		return (
			<Checkbox
				id={path}
				label={checkBoxlabel}
				onChange={onChange}
				disabled={!enabled}
				value={data || ""}
				checked={data ?? false}
				aria-label={checkBoxlabel || "checkbox item"}
			/>
		);
	}, [data, enabled, label, onChange, path]);

	return (
		<CheckboxControlWrapper>
			<FormField
				fieldHelper={{
					msg: (
						<>
							{uischema?.subText && (
								<>
									{parse(findAndTransformHTMLString(uischema.subText))}
									<br />
								</>
							)}
							{label === "Multi" && (
								<>
									Enable this checkbox if this field can have a long text
									<br />
								</>
							)}
							{description && (
								<>
									{description}
									<br />
								</>
							)}
							{canEnableOnly && (
								<>
									You don't have permisson to disable properties. Please cancel the changes if you don't want to save
									this changes.
									<br />
								</>
							)}
						</>
					),
					id: "input-control-msg",
				}}
			>
				{checkbox}
			</FormField>
		</CheckboxControlWrapper>
	);
};

const CheckBoxControlComp = React.memo(CheckboxControl);

export default withJsonFormsControlProps(CheckBoxControlComp);
